// Lib
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { EditorContent } from '@tiptap/react';
import { noop } from 'lodash';

// Utils
import { getElementId } from '../../../../common/elements/utils/elementPropertyUtils';
import { getMainEditorId, getMainEditorKey } from '../../utils/elementEditorUtils';
import { canWrite } from '../../../../common/permissions/permissionUtil';

// Types
import type { ImMNElement } from '../../../../common/elements/elementModelTypes';
import type { SaveTiptapEditorContentFunction } from '../../../../common/tiptap/extensions/externalStoreSync/externalStoreSynchroniserTypes';
import type { TiptapContent } from '../../../../common/tiptap/tiptapTypes';

// Hooks
import useTaskListEditorExtensions from './useTaskListEditorExtensions';
import useTiptapInnerRef from '../../../../common/tiptap/hooks/useTiptapInnerRef';
import usePoiUpdateHandler from '../../../components/pointsOfInterest/usePoiUpdateHandler';
import useClientElementTiptapEditor from '../../../components/tiptapEditor/useClientElementTiptapEditor';
import useTaskListFeatureSuggestionTriggers from '../suggestion/singleEditor/useTaskListFeatureSuggestionTriggers';

// Components
import { SearchHighlightObserver } from '../../../../common/tiptap/extensions/SearchHighlighter';
import TaskListDropTarget from './TaskListDropTarget';

// Styles
import './TaskListEditor.scss';

export type TaskListEditorProps = {
    element: ImMNElement;
    textContent: TiptapContent | null;
    saveContent: SaveTiptapEditorContentFunction;
    isEditable: boolean;
    isEditing: boolean;
    isSingleSelected: boolean;
    isFocusedForegroundElement?: boolean;
    isPresentational?: boolean;
    currentEditorId?: string;
    startEditing: (args: { editorId: string; editorKey: string }) => void;
    spellCheck: boolean;
    permissions?: number;
    moveToTrashHandler?: (args: { elementId: string }) => unknown;
    setParentHoveredChildAcceptsDrop?: (hoveredChildAcceptsDrop: boolean) => void;
};

/**
 * Stands in for the drop target on presentational task lists (e.g. board exports).
 * They render outside the workspace's DnD provider, where react-dnd hooks throw.
 */
const TaskListEditorPresentational: React.FC<React.ComponentProps<typeof TaskListDropTarget>> = ({ children }) => (
    <>{children({ connectDropTarget: noop, dropIndicator: null })}</>
);

/**
 * Renders a Tiptap task list editor wired into Milanote's element editor lifecycle.
 */
const TaskListEditor: React.FC<TaskListEditorProps> = (props) => {
    const {
        element,
        textContent,
        saveContent,
        isEditable,
        isEditing,
        isSingleSelected,
        isFocusedForegroundElement,
        isPresentational,
        currentEditorId,
        startEditing,
        spellCheck,
        permissions,
        moveToTrashHandler,
        setParentHoveredChildAcceptsDrop,
    } = props;

    const elementId = getElementId(element);

    const { innerRef, contentInnerRef } = useTiptapInnerRef();
    const onHighlightsChanged = usePoiUpdateHandler({ contentInnerRef, elementId });

    // Both backspacing an emptied list and deleting its last task leave nothing to show.
    // Left undefined where the element can't be trashed (e.g. Storybook), so those deletions
    // no-op instead of reporting a removal that never happens.
    const moveTaskListToTrash = useMemo(
        () => (moveToTrashHandler ? () => moveToTrashHandler({ elementId }) : undefined),
        [elementId, moveToTrashHandler],
    );

    // Board write-permission is stable for the editor's lifetime (unlike `isEditable`,
    // which flips as the user enters/leaves editing), so it can be baked into the
    // extension config and read by the task item node views without a React context.
    // Presentational lists (e.g. board exports) are display-only, so they get no drag handles.
    const canModifyContent = !isPresentational && (permissions === undefined || canWrite(permissions));

    const dispatch = useDispatch();

    const extensions = useTaskListEditorExtensions({
        saveContent,
        isEditable,
        dispatch,
        onHighlightsChanged,
        onEmptyBackspace: moveTaskListToTrash,
        canModifyContent,
        elementId,
        moveTaskListToTrash,
        trackInsertions: true,
    });

    const editorIdArgs = { element, isFocusedForegroundElement };
    const editorId = getMainEditorId(editorIdArgs);
    const editorKey = getMainEditorKey(editorIdArgs);

    const { editor, onMouseDown, onClick } = useClientElementTiptapEditor({
        extensions,
        persistedContent: textContent,

        editorId,
        editorKey,
        currentEditorId,

        isEditable,
        isEditing,
        isSingleSelected,

        startEditing,
        saveContent,
        registryEditorId: editorId,
    });

    useTaskListFeatureSuggestionTriggers({ editor, element });

    // Either the presentational wrapper (no DnD) or the drop target (with DnD)
    const TaskListEditorWrapper = isPresentational ? TaskListEditorPresentational : TaskListDropTarget;

    return (
        <TaskListEditorWrapper
            editor={editor}
            elementId={elementId}
            setParentHoveredChildAcceptsDrop={setParentHoveredChildAcceptsDrop}
        >
            {({ connectDropTarget, dropIndicator }) => (
                <div
                    ref={connectDropTarget}
                    className="TaskListEditorContainer"
                    onMouseDown={onMouseDown}
                    onClick={onClick}
                >
                    <SearchHighlightObserver editor={editor} />
                    <EditorContent
                        innerRef={innerRef}
                        className="TaskListEditor"
                        editor={editor}
                        dir="auto"
                        spellCheck={spellCheck}
                    />
                    {dropIndicator && <div className="TaskListDropIndicator" style={dropIndicator} />}
                </div>
            )}
        </TaskListEditorWrapper>
    );
};

export default TaskListEditor;
